/**
 * /config → Knowledge base: which row carries the late-arrival rule
 * (`late_arrival_rule_topic`, member-running-late, 2026-10-19).
 *
 * The rule names its topic by slug, but while someone types, a slug is no
 * row's identity. A new topic passes through the rule's exact slug on the way
 * to a longer one ("late-arrival" on the way to "late-arrival-home"); the
 * rule's own row passes through "" when its name is cleared and retyped, and
 * through another row's exact name when renamed past it. Matched by name, the
 * next keystroke took the rule onto the new row and deleting that half-typed
 * duplicate cleared it (the pre-deploy review); matched by name on the topic's
 * only row, a cleared name dropped the rule and a rename past another row's
 * name left it naming that row (the review of that fix). So the editor keeps
 * the row it last saw the rule on: a rename moves the rule, and a delete
 * clears it, only on that row. The page holds that track, so it outlives the
 * section while another one is open, and it is taken for one topic only, so
 * a config loaded or picked since never inherits it (`ruleTrackFor`).
 */

type KnowledgeBaseRows = ReadonlyArray<{ readonly topic: string }>;

/**
 * The rule's topic (`null` = none) and the row the editor last saw it on:
 * `null` = not seen yet, {@link NO_ROW} = seen with no row carrying it.
 */
export interface LateArrivalRuleTrack {
  readonly topic: string | null;
  readonly row: number | null;
}

/**
 * A rule seen naming a topic no row carries (written elsewhere, shown as
 * "(not found)") stays without a row until it is picked again: a new topic
 * typed through its name would otherwise take it, and the Save's refusal
 * with it (the review of the reviews' fixes).
 */
export const NO_ROW = -1;

/** The track for the rule's topic now: the row seen, if it was seen for that topic. */
export function ruleTrackFor(
  topic: string | null,
  seen: LateArrivalRuleTrack | null,
): LateArrivalRuleTrack {
  return { topic, row: seen !== null && seen.topic === topic ? seen.row : null };
}

/**
 * The row the rule names: the row last seen while it still carries the rule's
 * topic, else the first row that does; `null` when there is no rule or no row
 * carries it.
 */
export function lateArrivalRuleRow(
  entries: KnowledgeBaseRows,
  track: LateArrivalRuleTrack,
): number | null {
  if (track.topic === null || track.row === NO_ROW) return null;
  if (track.row !== null && entries[track.row]?.topic === track.topic) {
    return track.row;
  }
  const first = entries.findIndex((e) => e.topic === track.topic);
  return first === -1 ? null : first;
}

/** The track as seen now: its row, or {@link NO_ROW} for a rule no row carries. */
function seenNow(entries: KnowledgeBaseRows, track: LateArrivalRuleTrack): LateArrivalRuleTrack {
  const row = lateArrivalRuleRow(entries, track);
  return { topic: track.topic, row: track.topic !== null && row === null ? NO_ROW : row };
}

/**
 * Row `index`'s topic is now `topic`: the rule follows when it is the rule's
 * row. Either way the rule's row is kept, so a duplicate typed later cannot
 * pass for it.
 */
export function trackRuleOnRename(
  entries: KnowledgeBaseRows,
  track: LateArrivalRuleTrack,
  index: number,
  topic: string,
): LateArrivalRuleTrack {
  const seen = seenNow(entries, track);
  return seen.row === index ? { topic, row: index } : seen;
}

/**
 * Row `index` is deleted: no rule when it was the rule's row, and a rule
 * below it moves up one with its row.
 */
export function trackRuleOnDelete(
  entries: KnowledgeBaseRows,
  track: LateArrivalRuleTrack,
  index: number,
): LateArrivalRuleTrack {
  const seen = seenNow(entries, track);
  if (seen.row === index) return { topic: null, row: null };
  return {
    topic: seen.topic,
    row: seen.row !== null && seen.row !== NO_ROW && index < seen.row ? seen.row - 1 : seen.row,
  };
}
